import { jsx } from 'react/jsx-runtime';
import { Toast } from '@freighthub/silhouette-core';
import { useRef, useState, useEffect, createContext, useContext } from 'react';

/******************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */

var __assign = function() {
    __assign = Object.assign || function __assign(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};

function __read(o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
}

typeof SuppressedError === "function" ? SuppressedError : function (error, suppressed, message) {
    var e = new Error(message);
    return e.name = "SuppressedError", e.error = error, e.suppressed = suppressed, e;
};

var SNACK_TYPE_INFO = 'info';
var SNACK_TYPE_ERROR = 'error';
var SNACK_TYPE_SUCCESS = 'success';
var useSnackServiceHook = function () {
    var delay = 250;
    var scheduled = useRef(null);
    var _a = __read(useState(), 2), current = _a[0], setCurrent = _a[1];
    var _b = __read(useState(), 2), next = _b[0], setNext = _b[1];
    var hideSnack = function () {
        setCurrent(null);
    };
    var swapSnack = function () {
        if (scheduled.current) {
            clearTimeout(scheduled.current);
        }
        if (!next) {
            hideSnack();
        }
        else {
            setCurrent(next);
        }
    };
    var addSnack = function (snack) {
        setNext(typeof snack === 'string' ? { message: snack } : snack);
    };
    var showSuccess = function (message) {
        addSnack({ message: message, type: SNACK_TYPE_SUCCESS });
    };
    var showError = function (message) {
        addSnack({ message: message, type: SNACK_TYPE_ERROR });
    };
    var showInfo = function (message) {
        addSnack({ message: message, type: SNACK_TYPE_INFO });
    };
    var addSnackSaved = function (name) {
        addSnack("Successfully saved ".concat(name, "!"));
    };
    useEffect(function () {
        if (next) {
            if (current) {
                setTimeout(function () {
                    swapSnack();
                }, delay);
            }
            else {
                swapSnack();
            }
        }
    }, [next]);
    useEffect(function () {
        if (current) {
            setNext(null);
            scheduled.current = window.setTimeout(function () {
                setTimeout(function () {
                    hideSnack();
                }, delay);
            }, 5000);
        }
    }, [current]);
    return {
        current: current,
        addSnack: addSnack,
        hideSnack: hideSnack,
        addSnackSaved: addSnackSaved,
        showSuccess: showSuccess,
        showError: showError,
        showInfo: showInfo
    };
};

var SnackContext = createContext(undefined);
function useSnackService() {
    var snackService = useContext(SnackContext);
    if (!snackService) {
        throw new Error('Must use useSnackService within an SnackContext');
    }
    return snackService;
}
var SnackContextProvider = function (_a) {
    var children = _a.children;
    var snack = useSnackServiceHook();
    return jsx(SnackContext.Provider, __assign({ value: snack }, { children: children }));
};

var Snack = function () {
    var snack = useSnackService();
    var snackItem = snack.current;
    if (!snackItem || !snackItem.message) {
        return null;
    }
    return (jsx(Toast, { message: snackItem.message, open: !!snack.current, onClose: snack.hideSnack, variant: snackItem.type || SNACK_TYPE_INFO }));
};

export { Snack, SnackContext, SnackContextProvider, useSnackService };
//# sourceMappingURL=index.es.js.map
